import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faServer, 
  faChartLine, 
  faUsers, 
  faGlobe 
} from '@fortawesome/free-solid-svg-icons';

gsap.registerPlugin(ScrollTrigger);

const StatsSection = () => {
  const sectionRef = useRef(null);
  const statsRef = useRef([]);
  const [animatedValues, setAnimatedValues] = useState([0, 0, 0, 0]);

  const stats = [
    {
      icon: faUsers,
      value: 13800,
      suffix: '+',
      label: '服务企业数量',
      description: '累计服务物流企业'
    },
    {
      icon: faServer,
      value: 2.8,
      suffix: '万亿',
      label: '处理交易金额',
      description: '年度处理交易总额'
    },
    {
      icon: faChartLine,
      value: 99.9,
      suffix: '%',
      label: '系统稳定性',
      description: '7x24小时稳定运行'
    },
    {
      icon: faGlobe,
      value: 365,
      suffix: '天',
      label: '全年无休',
      description: '持续技术支持'
    }
  ];

  useEffect(() => {
    const section = sectionRef.current;
    
    // 创建滚动触发动画
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        end: 'bottom 20%',
        toggleActions: 'play none none reverse'
      }
    });

    // 数字动画
    stats.forEach((stat, index) => {
      tl.to({}, {
        duration: 2,
        ease: 'power2.out',
        onUpdate: function() {
          const progress = this.progress();
          const currentValue = stat.value * progress;
          
          setAnimatedValues(prev => {
            const newValues = [...prev];
            newValues[index] = currentValue;
            return newValues;
          });
        }
      }, index * 0.2);
    });

    // 卡片入场动画
    tl.fromTo(
      statsRef.current,
      { y: 50, opacity: 0, scale: 0.9 },
      { y: 0, opacity: 1, scale: 1, duration: 0.8, stagger: 0.1, ease: 'back.out(1.7)' },
      0
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const formatValue = (value, index) => {
    const stat = stats[index];
    if (stat.suffix === '万亿') {
      return value.toFixed(1);
    } else if (stat.suffix === '%') {
      return value.toFixed(1);
    } else {
      return Math.floor(value).toLocaleString();
    }
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-primary-dark via-blue-900 to-primary-blue relative overflow-hidden">
      {/* 背景粒子效果 */}
      <div className="absolute inset-0 particles-container">
        <div className="absolute top-10 left-10 w-2 h-2 bg-primary-cyan rounded-full animate-ping"></div>
        <div className="absolute top-32 right-20 w-1 h-1 bg-white rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 left-1/4 w-3 h-3 bg-primary-cyan/50 rounded-full animate-bounce"></div>
        <div className="absolute bottom-32 right-1/3 w-1 h-1 bg-white rounded-full animate-ping"></div>
        <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-primary-cyan/30 rounded-full animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* 标题 */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            数据说话，实力见证
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            云帐天下凭借强大的技术实力和丰富的行业经验，为物流快运企业提供专业的财务解决方案
          </p>
        </div>

        {/* 统计数据网格 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              ref={el => statsRef.current[index] = el}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300 transform hover:scale-105 border border-white/20"
            >
              {/* 图标 */}
              <div className="w-16 h-16 bg-gradient-to-br from-primary-cyan to-primary-blue rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <FontAwesomeIcon 
                  icon={stat.icon} 
                  className="text-white text-2xl" 
                />
              </div>
              
              {/* 数值 */}
              <div className="mb-4">
                <span className="text-4xl md:text-5xl font-bold text-white block">
                  {formatValue(animatedValues[index], index)}
                </span>
                <span className="text-2xl font-semibold text-primary-cyan">
                  {stat.suffix}
                </span>
              </div>
              
              {/* 标签 */}
              <h3 className="text-xl font-semibold text-white mb-2">
                {stat.label}
              </h3>
              
              {/* 描述 */}
              <p className="text-blue-200 text-sm">
                {stat.description}
              </p>
            </div>
          ))}
        </div>

        {/* 底部装饰 */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-4 bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 border border-white/20">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-white font-medium">系统实时运行中</span>
            <div className="w-2 h-2 bg-primary-cyan rounded-full animate-ping"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;